import {
  useJ1AuthFetch,
  useAsyncEffect,
  retryIfTimeout,
  urls,
  IMfaPreferencesInput,
  IMfaSecret,
  IUser,
} from '@jupiterone/jupiter-web-toolkit';
import React, { createContext, useContext, useState } from 'react';

export const useUserLogic = () => {
  const { authFetch } = useJ1AuthFetch();
  const [user, setUser] = useState<IUser>();
  const [normalizedUserName, setNormalizedUserName] = useState('');
  const [loading, setLoading] = useState<boolean>(true);

  async function getUser(): Promise<IUser> {
    const user = await retryIfTimeout(() => {
      return authFetch(`${urls.user}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    });

    if (!user.mfaSettings) {
      user.mfaSettings = {
        settingList: [],
        preferredMethod: '',
      };
    }
    return user;
  }

  useAsyncEffect(async () => {
    await refetch();
  }, []);

  const refetch = async () => {
    const user = await getUser();
    setUser(user);
    setLoading(false);
    return user;
  };

  function getNormalizedUserName(user: IUser) {
    const firstName = user?.profile.givenName;
    const lastName = user?.profile.familyName;
    let userName = '';

    if (firstName) {
      userName = firstName;
      if (lastName) {
        userName = userName.concat(' ', lastName);
      }
    } else {
      userName = user?.profile.name || user?.id || 'N/A';
    }
    return userName;
  }

  async function updateProfileName(name: string): Promise<IUser> {
    const response = await authFetch(`${urls.user}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ profile: { name } }),
    });

    const newUser = await response.json();
    if (newUser) {
      setNormalizedUserName(getNormalizedUserName(newUser));
      setUser(newUser);
    }

    return newUser;
  }

  async function getMfaSecret(): Promise<IMfaSecret> {
    const response = await authFetch(`${urls.mfaSecret}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await response.json();
  }

  async function verifyMfaCode(code: string): Promise<{ status: boolean }> {
    const response = await authFetch(`${urls.verifyCode}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code }),
    });

    return await response.json();
  }

  async function updateMfaPreferences(
    preferences: IMfaPreferencesInput
  ): Promise<{ status: boolean }> {
    const response = await authFetch(`${urls.updateMfaPreferences}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ preferences }),
    });

    return await response.json();
  }
  return {
    user,
    normalizedUserName,
    loading,
    refetch,
    updateProfileName,
    getMfaSecret,
    verifyMfaCode,
    updateMfaPreferences,
  };
};

export type UseUser = ReturnType<typeof useUserLogic>;
export const UserContext = createContext({} as UseUser);
export const useUser = () => useContext(UserContext);

export interface UseUserContextProviderProps {
  children: React.ReactNode;
}

export const UserProvider = ({ children }: UseUserContextProviderProps) => {
  const user = useUserLogic();

  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
};
